import { useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";

const supportedLanguages = ["fr", "en", "es"] as const;
type SupportedLanguage = typeof supportedLanguages[number];

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: "website" | "article";
  publishedTime?: string;
  author?: string;
  noIndex?: boolean;
  forceIndex?: boolean;
  paginationPrev?: string;
  paginationNext?: string;
}

const DEFAULT_TITLE = "Explaora - Planifiez et partagez vos voyages";
const DEFAULT_DESCRIPTION = "Créez, documentez et partagez vos aventures de voyage avec Explaora. Planifiez votre itinéraire, gérez votre budget et inspirez la communauté.";
const DEFAULT_IMAGE = "https://explaora.com/hero-travel.webp";
const SITE_URL = "https://explaora.com";

const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  url,
  type = "website",
  publishedTime,
  author = "Explaora",
  noIndex = false,
  forceIndex = false,
  paginationPrev,
  paginationNext,
}: SEOProps) => {
  const { lang } = useParams<{ lang: string }>();
  const location = useLocation();
  
  const currentLang = (lang && supportedLanguages.includes(lang as SupportedLanguage)) 
    ? lang as SupportedLanguage 
    : "fr";
  
  const fullTitle = title ? `${title} | Explaora` : DEFAULT_TITLE;
  
  // Get path without language prefix for hreflang generation
  const getPathWithoutLang = () => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    if (pathParts.length > 0 && supportedLanguages.includes(pathParts[0] as SupportedLanguage)) {
      return '/' + pathParts.slice(1).join('/');
    }
    return location.pathname;
  };

  // Build canonical URL: always use the current language prefix for consistency
  const getCanonicalUrl = () => {
    if (url) {
      // Always include language prefix, even when url prop is provided
      const cleanUrl = url.startsWith('/') ? url : `/${url}`;
      return `${SITE_URL}/${currentLang}${cleanUrl}`;
    }
    // Otherwise, build from current location with proper language prefix
    const pathWithoutLang = getPathWithoutLang();
    return `${SITE_URL}/${currentLang}${pathWithoutLang}`;
  };
  
  const fullUrl = getCanonicalUrl();

  useEffect(() => {
    // Update document title
    document.title = fullTitle;

    // Helper function to update or create meta tag
    const updateMetaTag = (selector: string, content: string, attribute: string = "content") => {
      let element = document.querySelector(selector) as HTMLMetaElement;
      if (element) {
        element.setAttribute(attribute, content);
      } else {
        element = document.createElement("meta");
        const [attrType, attrValue] = selector.replace("meta[", "").replace("]", "").split("=");
        element.setAttribute(attrType, attrValue?.replace(/"/g, "") || "");
        element.setAttribute(attribute, content);
        document.head.appendChild(element);
      }
    };

    // Helper function to update or create link tag
    const updateLinkTag = (rel: string, href: string, additionalAttrs?: Record<string, string>) => {
      const selector = additionalAttrs?.hreflang 
        ? `link[rel="${rel}"][hreflang="${additionalAttrs.hreflang}"]`
        : `link[rel="${rel}"]:not([hreflang])`;
      
      let element = document.querySelector(selector) as HTMLLinkElement;
      if (element) {
        element.href = href;
      } else {
        element = document.createElement("link");
        element.rel = rel;
        element.href = href;
        if (additionalAttrs) {
          Object.entries(additionalAttrs).forEach(([key, value]) => {
            element.setAttribute(key, value);
          });
        }
        document.head.appendChild(element);
      }
    };

    // Remove existing hreflang links before adding new ones
    const removeHreflangLinks = () => {
      const existingLinks = document.querySelectorAll('link[rel="alternate"][hreflang]');
      existingLinks.forEach(link => link.remove());
    };

    // Remove existing pagination links before adding new ones
    const removePaginationLinks = () => {
      document.querySelectorAll('link[rel="prev"], link[rel="next"]').forEach(link => link.remove());
    };

    // Basic meta tags
    updateMetaTag('meta[name="description"]', description);
    updateLinkTag("canonical", fullUrl);

    // Hreflang tags for multilingual SEO
    removeHreflangLinks();
    const pathWithoutLang = getPathWithoutLang();
    
    supportedLanguages.forEach((langCode) => {
      const langUrl = `${SITE_URL}/${langCode}${pathWithoutLang}`;
      updateLinkTag("alternate", langUrl, { hreflang: langCode });
    });
    
    // x-default pointing to French (default language)
    updateLinkTag("alternate", `${SITE_URL}/fr${pathWithoutLang}`, { hreflang: "x-default" });

    // Pagination rel="prev" / rel="next"
    removePaginationLinks();
    if (paginationPrev) {
      updateLinkTag("prev", `${SITE_URL}/${currentLang}${paginationPrev}`);
    }
    if (paginationNext) {
      updateLinkTag("next", `${SITE_URL}/${currentLang}${paginationNext}`);
    }

    // Robots
    if (noIndex) {
      updateMetaTag('meta[name="robots"]', "noindex, nofollow");
    } else if (forceIndex) {
      updateMetaTag('meta[name="robots"]', "index, follow");
    } else {
      const robotsMeta = document.querySelector('meta[name="robots"]');
      if (robotsMeta) {
        robotsMeta.remove();
      }
    }

    // Open Graph tags
    updateMetaTag('meta[property="og:type"]', type);
    updateMetaTag('meta[property="og:url"]', fullUrl);
    updateMetaTag('meta[property="og:title"]', fullTitle);
    updateMetaTag('meta[property="og:description"]', description);
    updateMetaTag('meta[property="og:image"]', image);
    updateMetaTag('meta[property="og:site_name"]', "Explaora");
    updateMetaTag('meta[property="og:locale"]', currentLang === "fr" ? "fr_FR" : currentLang === "es" ? "es_ES" : "en_US");

    // Twitter tags
    updateMetaTag('meta[name="twitter:card"]', "summary_large_image");
    updateMetaTag('meta[name="twitter:url"]', fullUrl);
    updateMetaTag('meta[name="twitter:title"]', fullTitle);
    updateMetaTag('meta[name="twitter:description"]', description);
    updateMetaTag('meta[name="twitter:image"]', image);

    // Article specific
    if (type === "article" && publishedTime) {
      updateMetaTag('meta[property="article:published_time"]', publishedTime);
    }
    if (type === "article" && author) {
      updateMetaTag('meta[property="article:author"]', author);
    }

    // Cleanup function to restore defaults when component unmounts
    return () => {
      document.title = DEFAULT_TITLE;
      const hreflangLinks = document.querySelectorAll('link[rel="alternate"][hreflang]');
      hreflangLinks.forEach(link => link.remove());
      document.querySelectorAll('link[rel="prev"], link[rel="next"]').forEach(link => link.remove());
    };
  }, [fullTitle, description, image, fullUrl, type, publishedTime, author, noIndex, forceIndex, currentLang, location.pathname, paginationPrev, paginationNext]);

  return null;
};

export default SEO;
